<script setup lang="ts">
import type { ChatMessage } from '@/composables/aiChatFunctions';
import type { AICollabMessage } from '@/types';
import { marked } from 'marked';
import { computed } from 'vue';

const props = defineProps<{
    message: ChatMessage | AICollabMessage,
    mine?: boolean,
    collab?: boolean
}>();

const formattedContent = computed(() => {
    marked.use({
        breaks:true,
        gfm: true,
    });
    return marked.parse(props.message.content);
});

</script>

<template>
    <div :class="{ 'ai': message.type === 'ai', 'human': message.type === 'human', 'other_user': !mine && collab }" class="message-container">
        <div class="message-content">
            <span v-if="message.type === 'ai'" class="image-container">
                <img class="animated" src="../../../assets/icons/ai.png">
            </span>
            <span v-if="message.type === 'human' && !mine && collab" class="profile-image-container">
                <img referrerpolicy="no-referrer" class="profile-image" :src="message.from?.picture" :alt="message.from?.name" :title="message.from?.name">
            </span>
            <div class="markdown-wrapper">
                <div v-html="formattedContent" class="markdown-content"></div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.message-container {
    max-width: 65%;
    padding: .5rem 1.6rem;
    border-radius: 10px;
    font-size: 1.7rem;
    line-height: 1.6;
    margin-bottom: 2rem;
    white-space: pre-line;
    display: flex;
    flex-direction: column;
}

.message-content:has(span) {
    display: flex;
    align-items: flex-start;
    gap: 1.6rem;
}

.message-content:hover{
    background-color: inherit !important;
    cursor: inherit !important;
}

.message-content{
    width: 100%;
}

.message-content p {
    display: inline-block;
    width: 100%;
}

.ai {
    color: black;
    align-self: flex-start;
    padding: 2rem 0;
    border-radius: 10px;
    max-width: 100%;
    width: 100%;
}

.ai p {
    padding-top: .5rem;
}

.human {
    background-color: #e9e9e980;
    color: black;
    align-self: flex-end;
    padding: 1rem 2rem;
}

.human.other_user {
    align-self: flex-start;
    padding: 1rem 2rem 1rem 0;
    background-color: transparent;
}

span.image-container {
    width: 3.6rem;
    height: 3.6rem;;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    border-radius: 50%;
    position: relative;
    background-image: linear-gradient(to right, #000435, #000273);
}

.profile-image {
    width: 3.6rem;
    height: 3.6rem;
    max-width: unset;
    border-radius: 50%;
}

span.image-container img {
    width: 2.4rem;
    height: 2.4rem;
    filter: brightness(0) invert(1);
}

.animated {
    animation: pulse 1s infinite alternate;
}

@keyframes pulse {
    0% {
        opacity: 60%;
    }

    100% {
        opacity: 100%;
    }
}

.markdown-wrapper {
    position: relative;
    width: 100%;
}

.markdown-content {
    width: 100%;
    display: inline-block;
}

.markdown-content :deep(p) {
    margin: 0;
    display: inline-block;
    width: 100%;
}

.markdown-content :deep(h1) {
    font-size: 2.4rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 1.2rem 0;
}

.markdown-content :deep(h2) {
    font-size: 2rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 1.2rem 0;
}

.markdown-content :deep(h3) {
    font-size: 1.8rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 1.2rem 0;
}

.markdown-content :deep(code) {
    background-color: #f6f8fa;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
}

.markdown-content :deep(pre) {
    background-color: #f6f8fa;
    padding: 1.5rem;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 1.4rem;
    margin: 1em 0;
}

.markdown-content :deep(pre code) {
    background-color: transparent;
    padding: 0;
    font-family: monospace;
    line-height: 1.5;
}

.markdown-content :deep(ul), .markdown-content :deep(ol) {
    padding-left: 2.5em;
}

.markdown-content :deep(ul) {
    list-style-type: disc;
    list-style-position: outside;
}

.markdown-content :deep(ul ul) {
    list-style-type: circle;
}

.markdown-content :deep(ul ul ul) {
    list-style-type: square;
}

.markdown-content :deep(ol) {
    list-style-type: decimal;
    list-style-position: outside;
}

.markdown-content :deep(ol ol) {
    list-style-type: lower-alpha;
}

.markdown-content :deep(ol ol ol) {
    list-style-type: lower-roman;
}

.markdown-content :deep(li) {
    margin: 0.3em 0;
    line-height: 1.2;
}

.markdown-content :deep(li > p) {
    margin: 0;
    display: unset;
    width: 100%;
}

.markdown-content :deep(a) {
    color: #0066cc;
    text-decoration: none;
}

.markdown-content :deep(a:hover) {
    text-decoration: underline;
}

.markdown-content :deep(table) {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
    font-size: 1.4rem;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
    border: 1px solid #dfe2e5;
    padding: 0.6em 1em;
    text-align: left;
}

.markdown-content :deep(th) {
    background-color: #f6f8fa;
    font-weight: 600;
}

.markdown-content :deep(tr:nth-child(even)) {
    background-color: #f6f8fa;
}

.markdown-content :deep(tr:hover) {
    background-color: #f0f0f0;
}

</style>
